import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Target, Award } from 'lucide-react';

const HomePage: React.FC = () => {
  const services = [
    {
      title: 'Design Engineering',
      description:
        'Comprehensive engineering design solutions from concept to completion',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
      link: '/services/design-engineering',
    },
    {
      title: 'Fabrication & Erection',
      description:
        'Expert fabrication and installation services for industrial applications',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
          />
        </svg>
      ),
      link: '/services/fabrication-erection',
    },
    {
      title: 'Machining',
      description:
        'Precision machining solutions with collaboration and backing of Sohar University',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      link: '/services/machining',
    },
    {
      title: 'Plant Maintenance',
      description:
        'Comprehensive maintenance solutions for industrial facilities',
      icon: (
        <svg
          className="w-8 h-8"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
      link: '/services/plant-maintenance',
    },
  ];

  const IMAGES = [
    '/clients/1.png',
    '/clients/2.png',
    '/clients/3.png',
    '/clients/4.png',
    '/clients/5.png',
    '/clients/6.png',
    '/clients/7.png',
  ];

  const coreValues = [
    'Positive Approach',
    'Respect',
    'Leadership',
    'Accountability',
    'Integrity',
    'Quality',
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner Section */}
      <div className="relative text-white overflow-hidden min-h-[70vh] flex items-center">
        {/* Background Hero Image */}
        <div className="absolute inset-0">
          <img
            src="/refinery.jpg"
            alt="Industrial Refinery - Engineering Excellence"
            className="w-full h-full object-cover"
          />
          {/* Overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>

        {/* Hero Content Overlay */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full z-10">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-6 animate-fade-in text-white">
              Engineering Excellence
              <span className="block text-gray-100">
                for Industrial Solutions
              </span>
            </h1>
            <p
              className="text-base md:text-xl text-gray-200 max-w-3xl mx-auto animate-slide-up"
              style={{ animationDelay: '0.2s' }}
            >
              Harmony Engineering Solution LLC - Your trusted partner for
              comprehensive engineering services across multiple industrial
              disciplines
            </p>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center mb-16 animate-fade-in"
            style={{ animationDelay: '0.5s' }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-500 mb-4">
              Our Services
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
              Comprehensive engineering solutions tailored to meet your
              industrial needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow duration-300 animate-slide-up cursor-pointer block"
                style={{ animationDelay: `${0.6 + index * 0.1}s` }}
              >
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center text-primary-500 mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-primary-500 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-700 text-sm mb-4">
                  {service.description}
                </p>
                <div className="text-primary-500 font-semibold hover:text-primary-600 transition-colors inline-flex items-center">
                  Learn More
                  <ArrowRight className="ml-1 h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>

          <div
            className="text-center mt-12 animate-fade-in"
            style={{ animationDelay: '1.0s' }}
          >
            <Link
              to="/services"
              className="bg-primary-500 text-secondary-500 px-8 py-3 rounded-lg font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
            >
              View All Services
            </Link>
          </div>
        </div>
      </div>

      {/* Our Clients Section */}
      <div className="py-16 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center mb-16 animate-fade-in"
            style={{ animationDelay: '1.1s' }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-500 mb-4">
              Our Clients
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
              Trusted by leading companies across various industries in Oman and
              beyond
            </p>
          </div>

          {/* Scrolling client logos */}
          <div className="relative w-full">
            <div className="flex animate-scroll gap-8">
              {IMAGES.map((img, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 bg-white rounded-lg p-4 shadow-md flex items-center justify-center w-40 h-24"
                >
                  <img
                    src={img}
                    alt={`Client ${index + 1}`}
                    className="object-contain w-full h-full"
                  />
                </div>
              ))}

              {/* Duplicate set for infinite loop effect */}
              {IMAGES.map((img, index) => (
                <div
                  key={`dup-${index}`}
                  className="flex-shrink-0 bg-white rounded-lg p-4 shadow-md flex items-center justify-center w-40 h-24"
                >
                  <img
                    src={img}
                    alt={`Client ${index + 1}`}
                    className="object-contain w-full h-full"
                  />
                </div>
              ))}
            </div>
          </div>

          <div
            className="text-center mt-12 animate-fade-in"
            style={{ animationDelay: '1.8s' }}
          >
            <p className="text-gray-600 text-sm md:text-base">
              Join our growing list of satisfied clients who trust us with their
              engineering needs
            </p>
          </div>
        </div>
      </div>

      {/* Vision, Mission & Core Values Section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center mb-16 animate-fade-in"
            style={{ animationDelay: '1.9s' }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-500 mb-4">
              Our Foundation
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto">
              Built on strong values and driven by clear vision and mission
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Vision */}
            <div
              className="bg-gray-50 rounded-lg p-8 shadow-md animate-slide-up"
              style={{ animationDelay: '2.0s' }}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                  <Target className="h-6 w-6 text-primary-500" />
                </div>
                <h3 className="text-2xl font-bold text-primary-500">
                  Our Vision
                </h3>
              </div>
              <div className="space-y-4 text-gray-700">
                <p className="text-sm md:text-base">
                  To increase our market value by producing leadership with our
                  positive approach in any conditions.
                </p>
                <p className="text-sm md:text-base">
                  Partner with client internal and external to produce success
                  for all.
                </p>
                <p className="text-sm md:text-base">
                  We wish to implement high level of commitment towards customer
                  satisfaction by performing at peak always.
                </p>
              </div>
            </div>

            {/* Mission */}
            <div
              className="bg-gray-50 rounded-lg p-8 shadow-md animate-slide-up"
              style={{ animationDelay: '2.1s' }}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                  <Users className="h-6 w-6 text-primary-500" />
                </div>
                <h3 className="text-2xl font-bold text-primary-500">Mission</h3>
              </div>
              <div className="space-y-4 text-gray-700">
                <p className="text-sm md:text-base">
                  Working as a team towards our motto:{' '}
                  <strong>'We partner your success'</strong>.
                </p>
                <p className="text-sm md:text-base">
                  We consider our customer most valued and only power source.
                </p>
                <p className="text-sm md:text-base">
                  We provide solutions with top quality workmanship, time
                  commitment.
                </p>
              </div>
            </div>

            {/* Core Values */}
            <div
              className="bg-gray-50 rounded-lg p-8 shadow-md animate-slide-up"
              style={{ animationDelay: '2.2s' }}
            >
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                  <Award className="h-6 w-6 text-primary-500" />
                </div>
                <h3 className="text-2xl font-bold text-primary-500">
                  Core Values
                </h3>
              </div>
              <div className="space-y-3">
                {coreValues.map((value, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 animate-fade-in"
                    style={{ animationDelay: `${2.3 + index * 0.1}s` }}
                  >
                    <CheckCircle className="h-5 w-5 text-primary-500 flex-shrink-0" />
                    <span className="text-sm md:text-base font-medium text-gray-800">
                      {value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4 animate-fade-in"
            style={{ animationDelay: '2.1s' }}
          >
            Ready to Partner with Us?
          </h2>
          <p
            className="text-lg md:text-xl text-secondary-100 mb-8 max-w-2xl mx-auto animate-slide-up"
            style={{ animationDelay: '2.2s' }}
          >
            Let's work together to bring your engineering projects to life with
            our expertise and commitment to excellence.
          </p>
          <div
            className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in"
            style={{ animationDelay: '2.3s' }}
          >
            <Link
              to="/contact"
              className="bg-secondary-500 text-primary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              Start Your Project
            </Link>
            <Link
              to="/about"
              className="border-2 border-secondary-500 text-secondary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-500 hover:text-primary-500 transition-colors cursor-pointer"
            >
              Learn About Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
